import { useContext, useEffect, useMemo, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import emptySelectOption from '@/constants/emptySelectOption';
import { TREATMENT_GROUP_BOOKING_PARAM } from '@/constants/treatmentGroups';
import AppContext from '@/contexts/appContext';
import FieldError from '@/forms/FieldError';
import { useAppointmentTypeOptions } from '@/hooks/useAppointmentTypes/useAppointmentTypes';
import useTimeZones from '@/hooks/useTimeZones';
import Accordion from '@/ui/components/Accordion';
import Alert from '@/ui/components/Alert';
import AutocompleteSingleSelect from '@/ui/components/AutocompleteSingleSelect';
import Container from '@/ui/components/Container';
import InputGroup from '@/ui/components/Form/InputGroup';
import { APPOINTMENTS_BOOKING_UI, hasPermission } from '@/utils/userPermission';

import PersonContext from '../Person/PersonContext';
import MemberAvailabilitySection from '../Profile/components/MemberAvailabilitySection';

import { GroupBooking } from './GroupBooking';
import { SlotBooking } from './SlotBooking';
import { BookingFilterFormValues } from './types';

export const Booking = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions } = useContext(AppContext);
  const {
    person: { time_zone, id: personId }
  } = useContext(PersonContext);

  const { data: timeZoneOptions } = useTimeZones(true);
  const formattedTimeZoneOptions = useMemo(() => {
    const data = timeZoneOptions?.data;
    if (data)
      return data.time_zones.map((timezone) => ({
        label: timezone,
        value: timezone
      }));
    return [];
  }, [timeZoneOptions]);

  const { data: appointmentTypeOptions } = useAppointmentTypeOptions(
    personId ? { person_id: personId } : undefined
  );
  const formattedAppointmentTypeOptions = useMemo(() => {
    if (appointmentTypeOptions)
      return appointmentTypeOptions.map((type) => ({
        label: type.name,
        value: String(type.healthie_id)
      }));
    return [];
  }, [appointmentTypeOptions]);

  const {
    control,
    watch,
    setValue,
    formState: { errors }
  } = useForm<BookingFilterFormValues>({
    defaultValues: {
      timeZone: time_zone || ''
    }
  });

  const selectedAppointmentType = watch('appointmentType');
  const selectedTimeZone = watch('timeZone');

  const selectedType =
    appointmentTypeOptions?.find((t) => String(t.healthie_id) === selectedAppointmentType) ?? null;

  const appointmentTypeInitialized = useRef(false);
  useEffect(() => {
    if (appointmentTypeInitialized.current) return;

    const appointmentTypeUUIDParam = searchParams.get('appointmentTypeId');
    if (appointmentTypeUUIDParam) {
      if (!appointmentTypeOptions) return;
      const matchedType = appointmentTypeOptions.find(
        (type) => type.id === appointmentTypeUUIDParam
      );
      if (!matchedType) return;
      setValue('appointmentType', String(matchedType.healthie_id));
      appointmentTypeInitialized.current = true;
      return;
    }

    const appointmentTypeParam = searchParams.get('appointmentType');
    if (appointmentTypeParam) {
      setValue('appointmentType', appointmentTypeParam);
      appointmentTypeInitialized.current = true;
    }
  }, [searchParams, appointmentTypeOptions, setValue]);

  useEffect(() => {
    // keeps the URL's appointmentType in sync with the form value -- doesn't
    // touch treatmentGroupId, which only clears on a genuine user-driven type
    // change (see the Controller's onChange below), not on hydration from a
    // deep link
    if (!selectedAppointmentType) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('appointmentType', selectedAppointmentType);
        return params;
      },
      { replace: true }
    );
  }, [selectedAppointmentType, setSearchParams]);

  if (
    !hasPermission({
      requiredPermission: APPOINTMENTS_BOOKING_UI,
      permissions: permissions
    })
  ) {
    return (
      <div className="px-4">
        <Alert level="error">You do not have permission to access the booking UI</Alert>
      </div>
    );
  }

  return (
    <Container type="page">
      <Accordion defaultOpen={false} buttonText="Member Availability">
        <MemberAvailabilitySection />
      </Accordion>
      <div>
        <InputGroup>
          <Controller
            name="appointmentType"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <AutocompleteSingleSelect
                {...field}
                clearable={false}
                label="Appointment Type"
                options={formattedAppointmentTypeOptions}
                placeholder="Select an appointment type..."
                selectedOption={
                  formattedAppointmentTypeOptions.find((option) => option.value === field.value) ??
                  emptySelectOption
                }
                onChange={(option) => {
                  if (option) {
                    field.onChange(option.value);
                    setSearchParams(
                      (prev) => {
                        const params = new URLSearchParams(prev);
                        params.delete(TREATMENT_GROUP_BOOKING_PARAM);
                        return params;
                      },
                      { replace: true }
                    );
                  }
                }}
              />
            )}
          />
          <FieldError message={errors.appointmentType?.message} />
        </InputGroup>
        <InputGroup>
          <Controller
            name="timeZone"
            control={control}
            defaultValue={time_zone || ''}
            render={({ field }) => (
              <AutocompleteSingleSelect
                {...field}
                clearable={false}
                label="Scheduling in Timezone (Defaults to Member's)"
                options={formattedTimeZoneOptions}
                selectedOption={
                  formattedTimeZoneOptions.find((option) => option.value === field.value) ??
                  emptySelectOption
                }
                onChange={(option) => {
                  if (option) {
                    field.onChange(option.value);
                  }
                }}
              />
            )}
          />
          <FieldError message={errors.timeZone?.message} />
        </InputGroup>
      </div>

      {selectedType?.is_group ? (
        <GroupBooking appointmentType={selectedType} timeZone={selectedTimeZone} />
      ) : (
        <SlotBooking
          appointmentType={selectedType}
          appointmentTypeHealthieId={selectedAppointmentType}
          timeZone={selectedTimeZone}
        />
      )}
    </Container>
  );
};
